'use client'

import { useEffect, useState, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface GlobalSidebarProps {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  icon?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCloseButton?: boolean
  closeOnBackdropClick?: boolean
}

const widthClasses = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-full'
}

export function GlobalSidebar({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  width = 'lg',
  showCloseButton = true,
  closeOnBackdropClick = true
}: GlobalSidebarProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollbarWidth}px`
      // Do NOT set overflow on documentElement — iOS Safari blocks child scroll when html is overflow:hidden

      // Block background touchmove scroll only for elements outside the sidebar content
      const preventScroll = (e: TouchEvent) => {
        if (!(e.target as HTMLElement).closest('.global-sidebar-content')) {
          e.preventDefault()
        }
      }
      document.addEventListener('touchmove', preventScroll, { passive: false })

      return () => {
        document.removeEventListener('touchmove', preventScroll)
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
    }
  }, [isOpen])

  if (!mounted || !isOpen) return null

  const sidebarContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeOnBackdropClick ? onClose : undefined}
            onWheel={(e: React.WheelEvent) => e.preventDefault()}
            onTouchMove={(e: React.TouchEvent) => e.preventDefault()}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={cn(
              'absolute top-0 bottom-0 h-full w-full bg-background flex flex-col shadow-2xl overscroll-contain',
              'right-0 border-l',
              widthClasses[width]
            )}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onWheel={(e: React.WheelEvent) => e.stopPropagation()}
            onTouchMove={(e: React.TouchEvent) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4 flex-shrink-0">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {icon && (
                  <div className="rounded-lg p-2 bg-primary/10 text-primary flex-shrink-0">
                    {icon}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-semibold truncate">{title}</h2>
                  {subtitle && (
                    <p className="text-sm text-muted-foreground truncate">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="flex-shrink-0 ms-2"
                >
                  <X className="h-5 w-5" />
                </Button>
              )}
            </div>

            {/* Content */}
            <div
              className="global-sidebar-content flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain p-6"
              style={{ WebkitOverflowScrolling: 'touch' }}
              onWheel={(e) => {
                const target = e.currentTarget
                const atTop = target.scrollTop === 0
                const atBottom = target.scrollHeight - target.scrollTop === target.clientHeight
                if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) {
                  e.stopPropagation()
                }
              }}
            >
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="border-t px-6 py-4 bg-muted/30 flex-shrink-0">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )

  return createPortal(sidebarContent, document.body)
}
